import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FiArrowRight, FiEye, FiEyeOff } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';

const HERO_IMAGE =
  'https://images.unsplash.com/photo-1483985988355-763728e1935b?auto=format&fit=crop&w=1400&q=80';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const { login, signInWithGoogle } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email || !formData.password) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      setLoading(true);
      await login(formData.email, formData.password);
      toast.success('Login successful!');
      navigate('/');
    } catch (error) {
      toast.error(error.message || 'Failed to login');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setGoogleLoading(true);
      await signInWithGoogle();
      toast.success('Successfully signed in with Google!');
      navigate('/');
    } catch (error) {
      toast.error(error.message || 'Failed to sign in with Google');
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <div className="sc-container py-8 md:py-12">
      <div className="sc-auth-shell">
        <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr]">
          <aside className="hidden lg:block p-6 xl:p-8">
            <div className="group relative min-h-[700px] overflow-hidden rounded-[1.7rem] border border-white/20 sc-delay-lift">
              <img
                src={HERO_IMAGE}
                alt="Editorial fashion"
                className="h-full w-full object-cover sc-delay-scale grayscale-[18%] contrast-110"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-black/65 via-black/35 to-black/20" />

              <div className="absolute bottom-10 left-10 max-w-xl rounded-2xl border border-white/20 bg-white/10 p-8 backdrop-blur-md">
                <p className="text-xs font-bold tracking-[0.24em] text-white/80">CURATED COMMERCE</p>
                <h1 className="mt-4 font-['Playfair_Display'] text-5xl leading-tight text-white">
                  Refined Style,
                  <span className="block text-4xl font-normal italic text-white/90">Built for Modern Buyers</span>
                </h1>
                <p className="mt-5 border-l-2 border-[#DB4444] pl-4 text-sm leading-relaxed text-white/85">
                  Discover collections crafted for people who care about design, quality and confidence in every
                  purchase.
                </p>
              </div>
            </div>
          </aside>

          <section className="relative px-5 py-8 sm:px-8 sm:py-10 lg:px-12 lg:py-12">
            <div className="relative mx-auto w-full max-w-lg">
              <div
                className="mb-7 h-44 overflow-hidden rounded-2xl border border-slate-200 bg-cover bg-center lg:hidden"
                style={{ backgroundImage: `url(${HERO_IMAGE})` }}
              >
                <div className="flex h-full items-end bg-gradient-to-t from-black/65 to-transparent p-5">
                  <p className="font-['Playfair_Display'] text-2xl text-white">Elevate your shopping experience</p>
                </div>
              </div>

              <div className="mb-8">
                <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#DB4444]">Welcome Back</p>
                <h2 className="mt-3 font-['Playfair_Display'] text-4xl text-[#1A1A1A]">Sign In</h2>
                <p className="mt-2 text-sm font-medium text-slate-500">Use your account credentials to continue.</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-7">
                <div className="relative">
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder=" "
                    autoComplete="email"
                    className="sc-auth-field peer"
                    required
                  />
                  <label className="sc-auth-float">Email Address</label>
                </div>

                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder=" "
                    autoComplete="current-password"
                    className="sc-auth-field peer pr-10"
                    required
                  />
                  <label className="sc-auth-float">Password</label>
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute right-0 top-4 text-slate-400 sc-hover-smooth hover:text-[#DB4444]"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                  </button>
                </div>

                <div className="flex items-center justify-between pt-3">
                  <label className="inline-flex items-center gap-2 text-sm font-medium text-slate-500">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="h-4 w-4 rounded border-slate-300 text-[#DB4444] focus:ring-[#DB4444]"
                    />
                    Remember me
                  </label>

                  <Link
                    to="/forgot-password"
                    className="text-sm font-semibold text-slate-500 sc-hover-smooth hover:text-[#DB4444]"
                  >
                    Forgot password?
                  </Link>
                </div>

                <div className="flex items-center justify-between border-t border-slate-200 pt-7">
                  <p className="text-sm font-medium text-slate-500">Securely continue to ShopCircuit</p>
                  <button
                    type="submit"
                    disabled={loading}
                    className="group sc-hover-smooth inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#1A1A1A] text-white hover:scale-105 hover:bg-[#DB4444] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <FiArrowRight className="text-xl sc-hover-smooth group-hover:translate-x-1" />
                  </button>
                </div>
              </form>

              <div className="mt-10">
                <div className="flex items-center gap-3">
                  <div className="h-px flex-1 bg-slate-200" />
                  <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400">Or connect with</span>
                  <div className="h-px flex-1 bg-slate-200" />
                </div>

                <button
                  type="button"
                  onClick={handleGoogleSignIn}
                  disabled={googleLoading}
                  className="group sc-hover-smooth mt-5 inline-flex w-full items-center justify-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-600 hover:scale-[1.01] hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                  {googleLoading ? 'Signing in...' : 'Sign in with Google'}
                </button>
              </div>

              <p className="mt-8 text-center text-sm text-slate-500">
                New to ShopCircuit?{' '}
                <Link
                  to="/signup"
                  className="font-bold text-[#DB4444] sc-hover-smooth hover:text-[#1A1A1A]"
                >
                  Create an account
                </Link>
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Login;
